/* ============================================================================
   SISTEMA INTEGRADO — el SGS como columna vertebral

   Decisión del operador: **el SGS manda**. No porque la seguridad operacional
   sea más importante en abstracto, sino por una razón técnica concreta: el SGS
   razona con **barreras** —el modelo bowtie— y eso permite preguntar «¿qué
   defensa falló?» en vez de solo «¿qué pasó?».

   Esa pregunta sirve igual para un hallazgo de calidad, uno ambiental o uno de
   seguridad y salud. Por eso el SGS puede absorber al SIG, y no al revés.

   ══════════════════════════════════════════════════════════════════════════
   LAS CUATRO REGLAS DE LA UNIFICACIÓN

   1. **Un evento toca VARIAS dimensiones a la vez.** El formulario obligaba a
      elegir una: un derrame de combustible en plataforma es a la vez seguridad
      operacional, ambiental y ocupacional. Obligar a elegir hace que el evento
      se registre una vez y se pierda para las otras dos auditorías.

   2. **La barrera es lo que se analiza, no el hecho.** «Se cargó combustible
      contaminado» no dice nada accionable. «La barrera de muestreo de
      combustible no se aplicó» dice qué arreglar. Y una barrera que existía y
      no funcionó es distinta de una que no existía.

   3. **Reporta cualquiera.** Tripulación, mantenimiento, tierra, despacho, y
      también **cliente y pasajeros**. Un pasajero no es empleado: no se le
      puede exigir código de área ni nombre completo, y su reporte no puede
      quedar trabado porque falte un dato de empleado.

   4. **Quien gestiona el SGS asesora a la Gerencia General, no a la línea
      operacional.** Eso no es un organigrama decorativo: significa que un
      reporte sobre la operación NO puede cerrarlo quien la conduce. La
      aplicación no tiene control de acceso, pero **registra el rol de quien
      actúa** y advierte cuando hay conflicto.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

const texto = (v) => (typeof v === "string" ? v.trim() : "");

/**
 * Las dimensiones del Sistema Integrado.
 *
 * `sms` va primero y no es una más: es la columna. Las otras se marcan además,
 * no en lugar de.
 */
export const DIMENSIONES = [
  ["sms", "Seguridad Operacional (SMS)", "Afecta la seguridad del vuelo o de la aeronave."],
  ["calidad", "Calidad (ISO 9001)", "Afecta el servicio al cliente o el cumplimiento de procesos."],
  ["sso", "Seguridad y Salud Ocupacional (OHSAS 18001)", "Afecta la salud o integridad de las personas en el trabajo."],
  ["ambiente", "Medio Ambiente (ISO 14001)", "Genera o puede generar impacto ambiental."],
  ["avsec", "Security (AVSEC) y Mercancías Peligrosas", "Afecta la seguridad de la aviación civil o el transporte de mercancías peligrosas."],
];

export const DIMENSION_COLUMNA = "sms";

/**
 * Quién puede reportar.
 *
 * `empleado: false` marca a los que NO son personal: a ellos no se les puede
 * exigir datos de empleado ni obligarlos a seguir el trámite interno.
 */
export const ROLES_REPORTANTE = [
  ["tripulacion", "Tripulación de vuelo", true],
  ["mantenimiento", "Mantenimiento", true],
  ["tierra", "Personal de tierra", true],
  ["despacho", "Despacho / control operacional", true],
  ["sig", "Personal del SIG", true],
  ["otro-interno", "Otro personal de la empresa", true],
  ["cliente", "Cliente", false],
  ["pasajero", "Pasajero", false],
  ["tercero", "Tercero externo", false],
];

/** Roles de quien GESTIONA el registro. */
export const ROLES_GESTOR = [
  ["sms-manager", "SMS Manager / Gerente SIG", "Asesor directo de Gerencia General."],
  ["sig", "Personal del SIG", "Apoya la gestión del sistema."],
  ["gerencia", "Gerencia General", "Ejecutivo responsable."],
  ["linea", "Línea operacional", "Jefatura del área involucrada."],
];

/** ¿Este rol es personal de la empresa? */
export function esInterno(rol) {
  const r = ROLES_REPORTANTE.find(([c]) => c === texto(rol));
  return r ? r[2] : false;
}

/**
 * Estado de una barrera frente al evento.
 *
 * La distinción entre «existía y falló» y «no existía» es el corazón del
 * análisis: la primera es un problema de cumplimiento, la segunda de diseño.
 * Tratarlas igual lleva a la acción correctiva equivocada.
 */
export const ESTADO_BARRERA = [
  ["funciono", "Funcionó", "La barrera actuó y contuvo el evento.",
    "Registrar como buena práctica. No requiere acción."],
  ["fallo", "Existía y falló", "La barrera estaba definida pero no contuvo el evento.",
    "Problema de CUMPLIMIENTO o de eficacia: revisar por qué no funcionó."],
  ["no-aplicada", "Existía y no se aplicó", "La barrera está definida pero nadie la ejecutó.",
    "Problema de CUMPLIMIENTO: revisar capacitación, supervisión o carga de trabajo."],
  ["no-existia", "No existía", "No había ninguna barrera para este evento.",
    "Problema de DISEÑO: hay que crear la barrera."],
];

/** Barreras que el operador ya tiene declaradas, por dimensión. */
export const BARRERAS_TIPICAS = {
  sms: ["Inspección prevuelo", "Lista de verificación", "FRAT", "Briefing de tripulación",
    "Marcha FOD", "Muestreo de combustible", "Peso y balance", "Análisis meteorológico"],
  calidad: ["Control documental", "Verificación de procedimiento", "Inspección de calidad"],
  sso: ["Elementos de protección personal", "Charla de cinco minutos", "Permiso de trabajo"],
  ambiente: ["Contención de derrames", "Gestión de residuos", "Control de emisiones"],
  avsec: ["Control de acceso", "Inspección de carga", "Declaración de mercancías peligrosas"],
};

/**
 * Valida las dimensiones marcadas.
 *
 * Al menos una, y **la columna no se puede desmarcar sola**: si el evento no
 * afecta la seguridad operacional, hay que decir cuál otra sí afecta.
 */
export function validarDimensiones(dimensiones) {
  const errores = [];
  const avisos = [];

  const lista = Array.isArray(dimensiones)
    ? dimensiones.map(texto).filter(Boolean)
    : [];

  if (lista.length === 0) {
    errores.push("Marca al menos una dimensión del Sistema Integrado. "
      + "Un evento puede afectar varias a la vez.");
    return { valido: false, errores, avisos, dimensiones: [] };
  }

  const desconocidas = lista.filter((d) => !DIMENSIONES.some(([c]) => c === d));
  if (desconocidas.length > 0) {
    errores.push(`Dimensión desconocida: ${desconocidas.join(", ")}.`);
  }

  /* Un evento que no toca seguridad operacional es posible —un hallazgo
     documental, por ejemplo— pero conviene que quede explícito, porque la
     mayoría de los eventos operacionales SÍ la tocan y omitirla suele ser un
     descuido, no una decisión. */
  if (!lista.includes(DIMENSION_COLUMNA)) {
    avisos.push("Este reporte no está marcado como Seguridad Operacional. "
      + "Si el evento ocurrió durante la operación, revisa si corresponde marcarla.");
  }

  return {
    valido: errores.length === 0,
    errores,
    avisos,
    /* Sin duplicados y en el orden declarado, para que los informes salgan
       siempre iguales. */
    dimensiones: DIMENSIONES.map(([c]) => c).filter((c) => lista.includes(c)),
  };
}

/**
 * Valida el análisis de barreras.
 *
 * Es lo que hace al SGS más útil que un registro de hechos: obliga a decir qué
 * defensa falló, no solo qué pasó.
 */
export function validarBarreras(barreras) {
  const errores = [];
  const avisos = [];

  const lista = Array.isArray(barreras) ? barreras : [];
  if (lista.length === 0) {
    /* NO es un error: al cargar el reporte todavía no se analizó. El análisis
       viene después, en el canal. Pero se avisa, porque un registro que se
       cierra sin barreras analizadas no aportó nada. */
    avisos.push("Sin barreras analizadas todavía. El análisis se completa "
      + "durante el tratamiento del reporte.");
    return { valido: true, errores, avisos, barreras: [] };
  }

  const limpias = [];
  for (const b of lista) {
    const nombre = texto(b?.nombre);
    const estado = texto(b?.estado);

    if (!nombre) { errores.push("Hay una barrera sin nombre."); continue; }
    if (!ESTADO_BARRERA.some(([c]) => c === estado)) {
      errores.push(`«${nombre}»: falta decir si funcionó, falló, no se aplicó o no existía.`);
      continue;
    }
    limpias.push({ nombre, estado, dimension: texto(b?.dimension) || null, nota: texto(b?.nota) || null });
  }

  /* Si TODAS las barreras funcionaron pero hubo evento, algo falta: o hay una
     barrera que nadie identificó, o el evento pasó por un camino no previsto.
     Las dos cosas son hallazgos. */
  if (limpias.length > 0 && limpias.every((b) => b.estado === "funciono")) {
    avisos.push("Todas las barreras figuran como que funcionaron, pero el evento ocurrió. "
      + "Falta identificar la barrera que no estaba o el camino no previsto.");
  }

  return { valido: errores.length === 0, errores, avisos, barreras: limpias };
}

/**
 * Qué acción sugiere el conjunto de barreras.
 *
 * No decide: ordena. Una barrera que no existía es un problema de diseño y
 * pide crear algo; una que no se aplicó es de cumplimiento y pide revisar
 * capacitación o supervisión. Confundirlas lleva a la acción equivocada —
 * escribir otro procedimiento cuando el problema es que nadie sigue el que ya
 * hay.
 */
export function orientacionDeBarreras(barreras = []) {
  const lista = Array.isArray(barreras) ? barreras : [];
  if (lista.length === 0) return null;

  const cuenta = (e) => lista.filter((b) => b?.estado === e).length;
  const noExistia = cuenta("no-existia");
  const noAplicada = cuenta("no-aplicada");
  const fallo = cuenta("fallo");

  const partes = [];
  if (noExistia > 0) {
    partes.push(`${noExistia} barrera${noExistia > 1 ? "s" : ""} inexistente${noExistia > 1 ? "s" : ""}: `
      + "problema de DISEÑO, hay que crearla.");
  }
  if (noAplicada > 0) {
    partes.push(`${noAplicada} no se aplicó: problema de CUMPLIMIENTO, `
      + "revisar capacitación, supervisión o carga de trabajo.");
  }
  if (fallo > 0) {
    partes.push(`${fallo} existía y falló: revisar si la barrera es eficaz para este riesgo.`);
  }

  return partes.length > 0 ? partes.join(" ") : "Las barreras contuvieron el evento.";
}

/**
 * ¿Hay conflicto de interés en quien va a cerrar el registro?
 *
 * El SMS Manager asesora a la Gerencia General, no a la línea operacional.
 * Eso significa que **un reporte sobre la operación no puede cerrarlo quien la
 * conduce**: sería juez y parte.
 *
 * La aplicación no tiene control de acceso — no puede impedirlo. Lo que hace
 * es **advertirlo y dejarlo registrado**, que es lo que un auditor busca.
 */
export function conflictoDeInteres(entrada) {
  /* Se desestructura DESPUÉS de comprobar: `{...} = null` revienta, y este
     módulo lo llama la pantalla con lo que haya. */
  const { rolGestor, dimensiones = [], rolReportante } = (entrada && typeof entrada === "object") ? entrada : {};
  const gestor = texto(rolGestor);
  const dims = Array.isArray(dimensiones) ? dimensiones : [];

  if (gestor === "linea" && dims.includes(DIMENSION_COLUMNA)) {
    return {
      hay: true,
      motivo: "Un reporte de seguridad operacional lo está cerrando la línea operacional. "
        + "El SMS Manager asesora a Gerencia General precisamente para que esto no pase: "
        + "quien conduce la operación no puede evaluar el reporte sobre ella.",
    };
  }

  /* Un reporte de un cliente cerrado por la línea que atiende a ese cliente
     tiene el mismo problema, por otro camino. */
  if (gestor === "linea" && texto(rolReportante) === "cliente") {
    return {
      hay: true,
      motivo: "Un reporte del cliente lo está cerrando la línea operacional. "
        + "Conviene que lo revise el SIG antes de cerrarlo.",
    };
  }

  return { hay: false, motivo: null };
}

/**
 * Requisitos según quién reporta.
 *
 * Un pasajero no es empleado: no tiene código de área, puede no querer dar su
 * nombre, y su reporte no puede quedar trabado porque falte un dato interno.
 */
export function requisitosDelRol(rol) {
  const r = texto(rol);
  const interno = esInterno(r);

  return {
    rol: r || null,
    interno,
    /* A los externos NO se les exige área ni identificación. Exigirla sería
       cerrarles la vía, y el manual dice que reporta cualquiera. */
    exigeArea: interno,
    exigeIdentificacion: false,
    /* Un reporte externo entra igual al canal, pero conviene que lo tome el
       SIG y no la línea: quien atiende al cliente no debería filtrar la queja
       del cliente. */
    derivarAlSig: !interno,
    nota: interno
      ? null
      : "Reporte externo: entra al canal como cualquier otro. No se le exigen "
        + "datos internos, y conviene que lo tome el SIG directamente.",
  };
}
